import React from 'react';
import { Select, SelectItem } from '@heroui/react';
import { useTranslation } from 'react-i18next';

export interface StatusOption {
  key: string;
  label: string;
}

interface StatusSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  options?: StatusOption[];
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showAllOption?: boolean;
  showEmptyOption?: boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({
  value,
  onChange,
  options,
  placeholder,
  isInvalid = false,
  errorMessage,
  isRequired = false,
  label,
  variant = 'bordered',
  className,
  'aria-label': ariaLabel,
  showAllOption = false,
  showEmptyOption = !showAllOption,
}) => {
  const { t } = useTranslation('admin');

  const selectLabel = label;
  const selectPlaceholder = placeholder || t('guides.table.columns.status');
  const selectAriaLabel = ariaLabel || (label === '' ? selectPlaceholder : undefined);

  // Default options: product statuses
  const defaultOptions: StatusOption[] = [
    { key: 'operational', label: t('common.status.operational') },
    { key: 'beta', label: t('common.status.beta') },
    { key: 'maintenance', label: t('common.status.maintenance') },
  ];

  const statusOptions = options || defaultOptions;

  const renderSelectItems = () => {
    return (
      <>
        {showAllOption && <SelectItem key="all">{t('guides.filters.all')}</SelectItem>}
        {showEmptyOption && <SelectItem key="">{t('common.none') || 'None'}</SelectItem>}
        {statusOptions.map((option) => (
          <SelectItem key={option.key}>{option.label}</SelectItem>
        ))}
      </>
    );
  };

  return (
    <Select
      label={selectLabel}
      placeholder={selectPlaceholder}
      selectedKeys={value !== undefined && value !== null ? [value] : []}
      onSelectionChange={(keys) => {
        const selectedValue = Array.from(keys)[0] as string;
        onChange(selectedValue ?? '');
      }}
      isRequired={isRequired}
      variant={variant}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      className={className}
      selectionMode="single"
      aria-label={selectAriaLabel || undefined}
    >
      {renderSelectItems()}
    </Select>
  );
};

export default StatusSelector;
